import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseAPI } from '../config';
import { toast } from 'react-toastify';
import userStore from '../store/userStore';
import { annotationAuthor, annotationRoleLabel } from '../utils/annotationAuthor';
import {
  SIDES,
  sideOf,
  sideLabelFor,
  recipientOptions,
  effectiveRecipients,
  canTargetUser,
} from '@claimup/shared/helpers/annotation-recipients';

// Color de cada nota según el lado que la escribe: ClaimUp, despacho o cliente.
const SIDE_STYLES = {
  [SIDES.CLAIMUP]: {
    card: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
    badge: 'bg-purple-100 text-purple-800',
  },
  [SIDES.PARTNER]: {
    card: 'bg-sky-50 border-sky-200 hover:bg-sky-100',
    badge: 'bg-sky-100 text-sky-800',
  },
  [SIDES.CLIENT]: {
    card: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100',
    badge: 'bg-emerald-100 text-emerald-800',
  },
};

const styleFor = role => SIDE_STYLES[sideOf(role)] || SIDE_STYLES[SIDES.CLIENT];

const listLabels = (sides, viewerRole) => sides.map(s => sideLabelFor(s, viewerRole)).join(' y ');

/** El nombre de la persona a la que va la nota, si va a alguien concreto. */
const targetName = nota => {
  const u = nota.para_usuario;
  if (!u || typeof u !== 'object') return null;
  return [u.username, u.userlastname].filter(Boolean).join(' ') || u.usermail || null;
};

const Note = ({ anotacion, onCompletedTask, activeRole, activeUserId }) => {
  const style = styleFor(anotacion.creator_role);
  const recipients = effectiveRecipients(anotacion);
  const persona = targetName(anotacion);
  const targetId = anotacion.para_usuario?._id || anotacion.para_usuario || null;
  // Se completa desde el lado al que va la tarea (un solo estado para todos). Si
  // va a una persona concreta, la completa ella.
  const isForMe =
    recipients.includes(sideOf(activeRole)) && (!targetId || String(targetId) === String(activeUserId));

  return (
    <div className={`rounded-lg border p-5 mb-4 transition-all duration-200 ${style.card}`}>
      <header className='flex justify-between items-start mb-3'>
        <div>
          <div className='flex flex-wrap items-center gap-2'>
            {/* El nombre de quien la escribió; el rol, al pasar el ratón. */}
            <span
              className={`text-xs px-2 py-1 rounded-full ${style.badge}`}
              title={annotationRoleLabel(anotacion, activeRole)}
            >
              {annotationAuthor(anotacion, activeRole)}
            </span>
            {anotacion.tarea === 'Pendiente' && (
              <span className='text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800'>👨‍💻 Pendiente</span>
            )}
            {anotacion.tarea === 'Completada' && (
              <span className='text-xs px-2 py-1 rounded-full bg-green-100 text-green-800'>✅ Completada</span>
            )}
            {anotacion.tarea !== 'No hay tarea' && (
              <span className='text-xs text-gray-500'>Para {persona || listLabels(recipients, activeRole)}</span>
            )}
            {anotacion.oculta_al_cliente && (
              <span className='text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700'>No la ve el cliente</span>
            )}
          </div>
          <div className='text-xs text-gray-500 mt-1'>
            {new Date(anotacion.fecha).toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
        {anotacion.tarea === 'Pendiente' && isForMe && (
          <div className='flex items-center space-x-2'>
            <label className='relative inline-flex items-center cursor-pointer'>
              <input type='checkbox' className='sr-only peer' onChange={() => onCompletedTask(anotacion._id)} />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              <span className='ml-3 text-sm font-medium text-gray-700'>Marcar como completada</span>
            </label>
          </div>
        )}
      </header>
      <div className='text-sm text-gray-700 space-y-2'>
        <p className='whitespace-pre-wrap'>{anotacion.anotacion}</p>
      </div>
    </div>
  );
};

const NotesExpedient = ({ idExpedient, expedientType }) => {
  const [anotaciones, setAnotaciones] = useState([]);
  const [anotacion, setAnotacion] = useState('');
  const [tarea, setTarea] = useState('No hay tarea');
  const { user } = userStore();

  // Para quién es la nota (docs/anotaciones-con-destinatario-plan.md): ClaimUp,
  // la empresa o, dentro de la empresa, una persona concreta. Las opciones salen
  // de la regla compartida; las personas, del servidor.
  const { options, defaults } = recipientOptions(user.role, expedientType);
  const defaultsKey = defaults.join(',');
  const [para, setPara] = useState(defaults);
  const [paraUsuario, setParaUsuario] = useState('');
  const [personas, setPersonas] = useState([]);
  useEffect(() => {
    // El tipo del expediente llega después de cargarlo: se recalculan los marcados.
    setPara(defaultsKey ? defaultsKey.split(',') : []);
  }, [defaultsKey]);

  // Las personas de la empresa. El despacho no las pide: su ruta no está en su lista blanca.
  const puedeElegirPersona = user.role === 'admin' || sideOf(user.role) === SIDES.CLIENT;
  useEffect(() => {
    if (!puedeElegirPersona || !idExpedient) return;
    axios
      .get(`${baseAPI}/annotations/recipients/${idExpedient}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then(({ data }) => setPersonas(data.people || []))
      .catch(() => setPersonas([]));
  }, [idExpedient, puedeElegirPersona]);

  const vaAlCliente = canTargetUser(para);

  const togglePara = side =>
    setPara(current => (current.includes(side) ? current.filter(s => s !== side) : [...current, side]));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${baseAPI}/annotations/${idExpedient}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setAnotaciones(response.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [idExpedient]);

  const handleCompletedTask = async id => {
    try {
      await axios.put(
        `${baseAPI}/annotations/completedtask/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success('Tarea completada', {
        position: 'top-right',
        autoClose: 2000,
      });
      setAnotaciones(
        anotaciones.map(anotacion => (anotacion._id === id ? { ...anotacion, tarea: 'Completada' } : anotacion))
      );
    } catch (error) {
      console.error(error);
      toast.error('Error al completar la tarea');
    }
  };

  // Antes de guardar una nota que no es tarea, se pregunta si debería serlo
  // (23/10/2026, pedido por Edgar): así nadie se olvida de dejar la tarea.
  const [preguntarTarea, setPreguntarTarea] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!anotacion.trim()) {
      toast.error('Por favor, escribe una anotación');
      return;
    }
    if (options.length > 0 && para.length === 0) {
      toast.error('Elige para quién es la anotación');
      return;
    }
    if (tarea !== 'Pendiente') {
      setPreguntarTarea(true);
      return;
    }
    await guardar(tarea);
  };

  const guardar = async tareaFinal => {
    setPreguntarTarea(false);
    const infoNote = {
      expedient_id: idExpedient,
      anotacion,
      fecha: new Date(),
      tarea: tareaFinal,
      ...(options.length > 0 ? { para } : {}),
      ...(vaAlCliente && paraUsuario ? { para_usuario: paraUsuario } : {}),
    };

    try {
      await axios.post(`${baseAPI}/annotations/`, infoNote, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setAnotacion('');
      setTarea('No hay tarea');
      setParaUsuario('');
      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || 'Error al añadir la anotación');
    }
  };

  // Lo que escribe el lado del cliente nunca le queda oculto: el aviso es para ClaimUp y el despacho.
  const hiddenFromClient = options.length > 0 && sideOf(user.role) !== SIDES.CLIENT && !para.includes(SIDES.CLIENT);

  return (
    <div className='col-span-full xl:col-span-8 bg-white shadow-lg rounded-lg border border-gray-200'>
      <header className='px-5 py-4 border-b border-gray-100'>
        <h2 className='font-semibold text-gray-800'>Anotaciones</h2>
      </header>

      <div className='p-5'>
        {anotaciones.length < 1 ? (
          <div className='text-center py-8'>
            <svg className='mx-auto h-12 w-12 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
              />
            </svg>
            <p className='mt-2 text-sm text-gray-500'>Las anotaciones que añadas aparecerán aquí</p>
          </div>
        ) : (
          <div className='space-y-4'>
            {anotaciones.map(nota => (
              <Note
                key={nota._id}
                anotacion={nota}
                onCompletedTask={handleCompletedTask}
                activeRole={user.role}
                activeUserId={user.id}
              />
            ))}
          </div>
        )}

        <form className='mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6' onSubmit={handleSubmit}>
          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Nueva anotación</label>
              <textarea
                className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                rows='4'
                placeholder='Escribe aquí tu anotación...'
                value={anotacion}
                onChange={e => setAnotacion(e.target.value)}
              />
            </div>

            {options.length > 0 ? (
              <div>
                <span className='block text-sm font-medium text-gray-700 mb-2'>Para</span>
                <div className='flex flex-wrap gap-4'>
                  {options.map(side => (
                    <label key={side} className='inline-flex items-center text-sm text-gray-700'>
                      <input
                        type='checkbox'
                        className='h-4 w-4 rounded-lg border-gray-300 text-blue-600 focus:ring-blue-500'
                        checked={para.includes(side)}
                        onChange={() => togglePara(side)}
                      />
                      <span className='ml-2'>{sideLabelFor(side, user.role)}</span>
                    </label>
                  ))}
                </div>
                {vaAlCliente && personas.length > 0 && (
                  <label className='mt-3 flex items-center gap-2 text-sm text-gray-700'>
                    <span>Persona</span>
                    <select
                      aria-label='Persona destinataria'
                      value={paraUsuario}
                      onChange={e => setParaUsuario(e.target.value)}
                      className='rounded-lg border border-gray-300 px-2 py-1 text-sm'
                    >
                      <option value=''>Toda la empresa</option>
                      {personas.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {sideOf(user.role) === SIDES.CLIENT && expedientType === 'judicial' && para.includes(SIDES.CLAIMUP) && (
                  <p className='mt-2 text-xs text-gray-500'>Lo que va a ClaimUp en un judicial le llega también al despacho.</p>
                )}
                {hiddenFromClient && <p className='mt-2 text-xs text-gray-500'>El cliente no verá esta anotación.</p>}
              </div>
            ) : (
              defaults.length > 0 && (
                <p className='text-xs text-gray-500'>Se enviará a {listLabels(defaults, user.role)}.</p>
              )
            )}

            <div className='flex items-center'>
              <label className='relative inline-flex items-center cursor-pointer'>
                <input
                  type='checkbox'
                  className='sr-only peer'
                  onChange={() => setTarea(tarea === 'Pendiente' ? 'No hay tarea' : 'Pendiente')}
                  checked={tarea === 'Pendiente'}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span className='ml-3 text-sm font-medium text-gray-700'>¿Es una tarea?</span>
              </label>
            </div>

            <div className='flex justify-end'>
              <button
                type='submit'
                className='inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200'
              >
                <svg
                  className='w-4 h-4 mr-2'
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <line x1='12' y1='5' x2='12' y2='19'></line>
                  <line x1='5' y1='12' x2='19' y2='12'></line>
                </svg>
                Añadir anotación
              </button>
            </div>
          </div>
        {preguntarTarea && (
              <div
                role='dialog'
                aria-label='¿Dejarla como tarea?'
                className='rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2'
              >
                <p className='font-medium'>¿Quieres dejarla como tarea?</p>
                <p className='text-xs'>Como tarea, a quien va dirigida le sale en sus tareas pendientes hasta que la complete.</p>
                <div className='flex flex-wrap gap-2'>
                  <button
                    type='button'
                    onClick={() => {
                      setTarea('Pendiente');
                      guardar('Pendiente');
                    }}
                    className='rounded-lg bg-blue-600 px-3 py-1.5 font-medium text-white hover:bg-blue-700'
                  >
                    Sí, como tarea
                  </button>
                  <button
                    type='button'
                    onClick={() => guardar('No hay tarea')}
                    className='rounded-lg border border-amber-300 px-3 py-1.5 font-medium hover:bg-amber-100'
                  >
                    No, solo la anotación
                  </button>
                  <button type='button' onClick={() => setPreguntarTarea(false)} className='px-3 py-1.5 text-amber-800 hover:underline'>
                    Volver
                  </button>
                </div>
              </div>
            )}
            </form>
      </div>
    </div>
  );
};

export default NotesExpedient;
